import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { TopicGrid } from "@/components/TopicGrid";
import { PopularArticlesList } from "@/components/PopularArticlesList";
import { Loader2, LifeBuoy } from "lucide-react";

export default function HomePage() {
  const [topics, setTopics] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/topics"),
      api.get("/articles", { params: { popular: true, limit: 8 } }),
    ])
      .then(([t, a]) => {
        setTopics(t.data);
        setPopular(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E8F0FE] via-[#F1F6FE] to-[#F8F9FA]" />
        <div className="absolute -top-24 right-1/4 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center">
          <span className="inline-block text-xs uppercase tracking-widest font-semibold text-[#1A73E8] mb-5">
            Gsupport Help Center
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]">
            How can we help you?
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
            Search our guides for Mail, Drive, Calendar, Account and Meet — or browse by topic below.
          </p>
          <div className="mt-9">
            <HeroSearch />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="py-24 grid place-items-center text-slate-400">
          <Loader2 className="w-7 h-7 animate-spin" />
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Topics */}
          <section className="py-14 sm:py-16">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-8">
              Browse help topics
            </h2>
            <TopicGrid topics={topics} />
          </section>

          {/* Popular */}
          <section className="pb-14">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-8">
              Popular articles
            </h2>
            <PopularArticlesList articles={popular} />
          </section>

          {/* Support CTA */}
          <section className="pb-20">
            <div className="rounded-2xl bg-white border border-slate-200 p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
              <div>
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">Still need a hand?</h3>
                <p className="text-slate-600 mt-1.5">Reach our team directly, or request to cancel your plan.</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/support" data-testid="home-support-cta" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold transition-colors">
                  <LifeBuoy className="w-5 h-5" /> Contact support
                </Link>
                <Link to="/cancel" data-testid="home-cancel-cta" className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors">
                  Cancel plan
                </Link>
              </div>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
